import api from "@/lib/api.ts";
import { getDeviceId } from "@/lib/get-device-id.ts";
import useUserStore from "@/store/user.store.ts";
import {
  Button,
  Divider,
  PasswordInput,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useMutation } from "@tanstack/react-query";
import { SubmitHandler, useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router";
import googleUrl from "@/assets/images/google.svg";
import { z, ZodType } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

type LoginForm = {
  email: string;
  password: string;
};

const loginFormSchema: ZodType<LoginForm> = z.object({
  email: z
    .string()
    .min(1, { message: "Email address is required" })
    .email("Invalid email address"),
  password: z.string().min(8, {
    message: "Password must be at least 8 characters long",
  }),
});

export function Login() {
  const { login } = useUserStore();
  const navigate = useNavigate();
  const deviceId = getDeviceId();
  const form = useForm<LoginForm>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });
  const errors = form.formState.errors;

  const mutation = useMutation({
    mutationFn: (loginForm: LoginForm) => {
      return api.post("auth/login", {
        ...loginForm,
        device_name: deviceId,
      });
    },
    onSuccess: (response) => {
      login(response.data.user, response.data.token);

      navigate("/");
    },
    retry: 0,
  });

  const onSubmit: SubmitHandler<LoginForm> = (data) => {
    mutation.mutate(data);
  };

  const handleGoogleLogin = () => {
    const webUrl = import.meta.env.VITE_WEB_URL;
    window.open(`${webUrl}/sso/google/redirect`, "_self");
  };

  return (
    <div className="mx-auto grid w-[350px] gap-6">
      <div className="grid gap-1 text-center">
        <Title order={1}>Welcome back</Title>
        <Text c="gray">Please enter your credentials</Text>
      </div>

      <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-6">
        <TextInput
          {...form.register("email")}
          type="email"
          name="email"
          withAsterisk
          label="Email address"
          size="md"
          error={errors.email?.message}
        />

        <PasswordInput
          {...form.register("password")}
          name="password"
          withAsterisk
          label="Password"
          size="md"
          error={errors.password?.message}
        />

        <Button type="submit" fullWidth size="md">
          Sign in
        </Button>
      </form>

      <Divider label="OR" labelPosition="center" />

      <Button
        type="button"
        onClick={handleGoogleLogin}
        variant="outline"
        size="md"
      >
        <img src={googleUrl} alt="Google" width="16" className="mr-2" />
        Sign in with Google
      </Button>

      <Text ta="center">
        Forgot your password?{" "}
        <Link to="/auth/forgot-password" className="underline">
          Reset
        </Link>
      </Text>

      <Text ta="center">
        Don&apos;t have an account?{" "}
        <Link to="/auth/register" className="underline">
          Sign up
        </Link>
      </Text>
    </div>
  );
}
